import { useSalePrefix } from '../lib/listingPaths';
import { Link } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';
import { ExploreTabs } from '../components/ExploreTabs';
import { ExploreHero } from '../components/ExploreHero';
import { SiteFooter } from '../components/SiteFooter';
import { PostingGate } from '../components/PostingGate';
import { useApi } from '../lib/useApi';
import { money } from '../lib/format';
import { Card } from '../components/ui';
import '../styles/public.css';

/**
 * "Sell your property" — the door a seller comes through.
 *
 * It states the price before asking for anything, because the two questions a
 * seller arrives with are "can I list here?" and "what does it cost?", and a
 * page that answers the second only after a sign-up is a page people leave.
 * The rate card is a public endpoint for exactly this reason.
 *
 * When nothing is priced the page says publishing is free rather than showing
 * an empty table — the same fail-open the domain implements, described in the
 * same words a seller would use.
 */

interface Rate {
  code: string; name: string; days: number; price: string;
  currency_code: string; is_featured: boolean; once_per_account: boolean;
}

export function SellProperty() {
  const salePrefix = useSalePrefix();
  const rates = useApi<{ rates: Rate[]; configured: boolean }>('/api/v1/sales/rates');
  const priced = (rates.data?.rates ?? []).filter((r) => Number(r.price) > 0);
  const free = (rates.data?.rates ?? []).filter((r) => Number(r.price) === 0);

  return (
    <>
      <PublicHeader />
      <main className="page public-page">
        <ExploreTabs path="/sell" />

        <ExploreHero
          section="buy"
          title="Sell your property"
          sub="Advertise a house, an apartment or a plot to buyers across Kenya."
        />

        <div className="post-choices">
          <PostingGate what="sell a property">
            <Link to={`${salePrefix}/new`} className="post-choice">
              <span className="sect-tile sect-buy post-choice-icon" aria-hidden>
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
                  strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20h14V9.5" />
                  <path d="M11 20v-5h2.5v5" /><circle cx="15.5" cy="12" r="1" />
                </svg>
              </span>
              <span className="post-choice-body">
                <span className="post-choice-title">List a property for sale</span>
                <span className="post-choice-blurb">
                  Houses, apartments, townhouses, commercial space and land.
                </span>
                <span className="post-choice-note">
                  Preparing the listing is free. You pay only to publish it, and only
                  once it has been approved.
                </span>
              </span>
              <span className="post-choice-go" aria-hidden>›</span>
            </Link>
          </PostingGate>
        </div>

        <Card title="How it works">
          <ol className="steps">
            <li>Tell us about the property: price, size, tenure and photographs.</li>
            <li>A person at NyumbaSuite reads it. That usually takes a working day.</li>
            <li>Once it is approved you buy a publication slot, and it goes on the site.</li>
            <li>Buyers enquire through the page. Your phone number appears only if you ask for it.</li>
          </ol>
          <p className="muted">
            Nobody pays for a listing that was refused: the money comes after the
            review, not before it.
          </p>
        </Card>

        <Card title="What it costs">
          {rates.loading ? <p className="muted">Checking…</p>
            : !rates.data?.configured ? (
              <p>
                <strong>Publishing is free at the moment.</strong> We are not charging for
                sale listings yet, so an approved property goes on the site at no cost.
              </p>
            ) : (
              <>
                {free.length > 0 && (
                  <p>
                    <strong>{free[0]!.name}</strong>, free for {free[0]!.days} days
                    {free[0]!.once_per_account && ', one per account'}.
                  </p>
                )}
                <div className="plan-grid">
                  {priced.map((r) => (
                    <div key={r.code} className="plan-card plan-card--buy">
                      <span className="plan-name">{r.name}</span>
                      <span className="plan-price">{money(r.price)}</span>
                      <span className="muted">
                        {r.days} days on Buy Property{r.is_featured ? ' · featured' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
        </Card>

        <Card title="Why we review everything">
          <p style={{ margin: 0 }}>
            Every property is read by somebody at NyumbaSuite before it goes live. It means
            your listing is not instant, and it means a buyer browsing here is looking at
            adverts a person has checked. We confirm a listing is real and legible; we do
            not verify title or value, and we are not agents in the sale.
          </p>
        </Card>
      </main>
      <SiteFooter />
    </>
  );
}
